import { Bell, CircleHelp, LogIn, LogOut, Moon, Sun } from "lucide-react";
import { useState } from "react";
import type { AppView } from "../types/workspace";
import type { Locale } from "../i18n";
import { t } from "../i18n";
import type { Theme } from "../hooks/useTheme";
import picassoOnLight from "../../assets/brand/bubstal-picasso-on-light.png";
import picassoOnDark from "../../assets/brand/bubstal-picasso-on-dark.png";

export function AppHeader({
  view,
  locale,
  theme,
  signedIn,
  onThemeToggle,
  onLocaleChange,
  onNavigate,
  onSignOut,
}: {
  view: AppView;
  locale: Locale;
  theme: Theme;
  onThemeToggle: () => void;
  signedIn: boolean;
  onLocaleChange: (locale: Locale) => void;
  onNavigate: (view: AppView) => void;
  onSignOut: () => void;
}) {
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const SessionIcon = signedIn ? LogOut : LogIn;
  return (
    <header className="app-header">
      <div className="brand-lockup" aria-label="Narrates Picasso">
        <span className="brand-mark">
          <img src="/narrates-logo.png" alt="Narrates" />
        </span>
        <span className="brand-name">Narrates</span>
        <span className="brand-slash">/</span>
        <h2 className="product-name">
          <img
            className="picasso-logo"
            src={theme === "dark" ? picassoOnDark : picassoOnLight}
            alt="Picasso"
          />
        </h2>
      </div>
      <nav className="header-nav" aria-label={t(locale, "mainNavigation")}>
        <button
          className={view === "workspace" ? "active" : ""}
          onClick={() => onNavigate("workspace")}
        >
          {t(locale, "workspace")}
        </button>
        <button
          className={view === "pricing" ? "active" : ""}
          onClick={() => onNavigate("pricing")}
        >
          {t(locale, "pricing")}
        </button>
      </nav>
      <div className="header-actions">
        <select
          aria-label="Language"
          value={locale}
          onChange={(event) => onLocaleChange(event.target.value as Locale)}
        >
          <option value="zh-Hant">繁中</option>
          <option value="en">EN</option>
          <option value="ja">日本語</option>
        </select>
        <button
          className="theme-toggle"
          aria-label={t(locale, "themeToggle")}
          aria-pressed={theme === "dark"}
          onClick={onThemeToggle}
        >
          {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
        </button>
        <button
          className="login-link"
          onClick={() => (signedIn ? onSignOut() : onNavigate("login"))}
        >
          <SessionIcon size={17} aria-hidden="true" />
          {t(locale, signedIn ? "logout" : "login")}
        </button>
        <button aria-label={t(locale, "help")}>
          <CircleHelp size={18} />
        </button>
        <div className="notification-control">
          <button
            className="notification-button"
            aria-label={t(locale, "notifications")}
            aria-expanded={notificationsOpen}
            aria-controls="notification-popover"
            onClick={() => setNotificationsOpen((open) => !open)}
          >
            <Bell size={18} />
          </button>
          {notificationsOpen && (
            <div
              id="notification-popover"
              className="notification-popover"
              role="status"
            >
              {t(locale, "noNotifications")}
            </div>
          )}
        </div>
        <span className="avatar" />
      </div>
    </header>
  );
}
